import { executionForResponse } from '@/analytics/requests';
import {
  beginSearch, contextForPageRequest, isCurrentSearch, navigationMethod,
  resultCount, resultsPageForResponse, resultsPageFromCache,
  type AppliedSearch, type ResultsPage, type SearchPageRequest, type SearchIntent,
} from '@/analytics/features/search-ui';
import {
  filterSnapshot, invalidFilterRanges, recordFilterApplyBlocked, recordFiltersApplied,
  rememberFilterOptions, type FilterMetadata,
} from '@/analytics/features/filters';
import { beginAction, currentAccountScope, sameAccount, immutableContext, type RequestAnalyticsContext } from '@/analytics/context';
import { getRequiredCredits, handlePeopleCreditError } from '@/utils/credits';
import { AnyAction, Effect, Reducer } from '@umijs/max';
import { message } from 'antd';
import {
  addPeopleToListV2,
  createPeopleListV2,
  fetchPeopleFromProductList,
  fetchPeopleFromProductListBoth,
  fetchPeopleFromProducts,
  fetchPeopleFromProductsBoth,
  fetchPeopleListsV2,
  fetchPerson,
  fetchPhone,
  fetchWorkEmail,
  searchPeople,
} from './service';
import type { PeopleUnlockCreditMetadata } from './sharedUnlock';
import {
  getPeopleCreditsAfterUnlock,
  getSharedUnlockMessage,
  isPeopleEmailUnlocked,
  isPeoplePhoneUnlocked,
} from './sharedUnlock';

export interface PersonSearchRequest {
  page?: number;
  role_title?: string;
  person_locations?: number[];
  person_name?: string;
  organizations?: number[];
  organization_employees_start?: number;
  organization_employees_end?: number;
  organization_revenue_start?: number;
  organization_revenue_end?: number;
  organization_industries?: number[];
  role_is_current?: boolean;
  person_skills?: number[];
  funding_types?: string[];
  funding_total_start?: number;
  funding_total_end?: number;
  // new / extended filters
  person_headline?: string;
  role_description?: string;
  role_position_start_date?: string;
  role_position_end_date?: string;
  role_years_on_position_start?: number;
  role_years_on_position_end?: number;
  organization_locations?: number[];
  organization_description?: string;
  organization_keywords?: number[];
  funding_event_date_featured_start?: string;
  funding_event_date_featured_end?: string;
  job_post_title?: string;
  job_post_description?: string;
  job_post_is_remote?: boolean | null;
  job_post_is_active?: boolean | null;
  job_post_date_featured_start?: string;
  job_post_date_featured_end?: string;
  job_post_locations?: number[];
  job_post_locations_exclude?: number[];
}

export interface SimplifiedPerson extends PeopleUnlockCreditMetadata {
  success?: boolean;
  record_id: number;
  person_id: number;
  person_photo: string | null;
  person_gender: string | null;
  name: string;
  role_title: string;
  location: string | null;
  linkedin_url: string | null;
  organization_id: number;
  organization_name: string;
  organization_legal_name: string;
  organization_logo: string | null;
  organization_employees_range: string | null;
  organization_employees_amount: number | null;
  organization_domain: string | null;
  viewed?: boolean;
  emails: string[] | string | null;
  work_email: string | null;
  work_email_status: string | null;
  date_updated: string | null;
  email_fetched_from: string | null;
  phone_numbers: string[] | null;
  email_unlocked?: boolean;
  phone_unlocked?: boolean;
}

export interface SimplifiedPersonSearchResponse {
  search_results: SimplifiedPerson[];
  total_search_results: number | null;
  countStatus?: 'ready' | 'unavailable';
}

export interface PeopleModelState {
  appliedSearch?: AppliedSearch<any>;
  appliedRequest?: Readonly<PersonSearchRequest>;
  pageRequest?: SearchPageRequest;
  resultsPage?: ResultsPage;
  searchIntent?: SearchIntent;
  filterMetadata?: FilterMetadata;
  contactResults?: Record<string, { analyticsContext?: RequestAnalyticsContext; execution?: ReturnType<typeof executionForResponse> }>;
  searchValue: string;
  filters: Partial<PersonSearchRequest>;
  peopleData: {
    pages: {
      [key: number]: {
        data: SimplifiedPerson[];
        total: number | null;
        runId?: string;
        pageSize?: number;
        analytics?: ResultsPage;
      };
    };
    total: number | null;
  };
  currentPage: number;
  pageSize: number;
  selectedRows: number[];
  filterDrawerVisible: boolean;
  peopleLists: any[];
  listModalVisible: boolean;
  createListModalVisible: boolean;
  addingToList: boolean;
  organizationOptions: Array<{ value: string; label: string }>;
}

interface PeopleModelType {
  namespace: 'people';
  state: PeopleModelState;
  effects: {
    fetchPeople: Effect;
    fetchPerson: Effect;
    fetchPeopleListsV2: Effect;
    createPeopleList: Effect;
    addPeopleToList: Effect;
    addAllPeopleToSelectedRows: Effect;
    getSelectedPeopleFromIds: Effect;
    fetchPeopleFromProductList: Effect;
    fetchPeopleFromProductListBoth: Effect;
    fetchPeopleFromProducts: Effect;
    fetchPeopleFromProductsBoth: Effect;
    changePage: Effect;
    fetchPhone: Effect;
    fetchWorkEmail: Effect;
  };
  reducers: {
    updateState: Reducer<PeopleModelState>;
    updateSearchValue: Reducer<PeopleModelState>;
    updateSearchValueOnly: Reducer<PeopleModelState>;
    setPersonsForPage: Reducer<PeopleModelState>;
    mergePersonDetails: Reducer<PeopleModelState>;
    resetFilters: Reducer<PeopleModelState>;
    toggleFilterDrawer: Reducer<PeopleModelState>;
    updateFilter: Reducer<PeopleModelState>;
    addToSelectedRows: Reducer<PeopleModelState>;
    removeFromSelectedRows: Reducer<PeopleModelState>;
    clearSelectedRows: Reducer<PeopleModelState>;
    setSelectedRows: Reducer<PeopleModelState>;
    setPeopleLists: Reducer<PeopleModelState>;
    toggleListModal: Reducer<PeopleModelState>;
    toggleCreateListModal: Reducer<PeopleModelState>;
    setAddingToList: Reducer<PeopleModelState>;
    initializeFromAccountsContext: Reducer<PeopleModelState>;
    resetAll: Reducer<PeopleModelState>;
  };
}


/** Availability is independent of the legacy business total (failure can still return zero).
 * Older responses without the additive status cannot establish evaluated emptiness. */
export function peopleResultCount(response: SimplifiedPersonSearchResponse) {
  if (response.countStatus !== undefined) {
    return resultCount(
      response.total_search_results,
      response.countStatus === 'ready' ? 'ready' : 'unavailable',
    );
  }
  return resultCount(
    response.total_search_results,
    response.total_search_results === 0 ? 'unavailable' : undefined,
  );
}

/** Compare the full provider cohort without changing form values or executed request types. */
function organizationCohort(value: unknown): string | undefined {
  if (!Array.isArray(value) || !value.length) return undefined;
  const ids: string[] = [];
  for (const member of value) {
    if (typeof member !== 'string' && typeof member !== 'number') return undefined;
    if (typeof member === 'number' && !Number.isSafeInteger(member)) return undefined;
    const raw = String(member);
    if (!/^\d{1,128}$/.test(raw)) return undefined;
    const id = raw.replace(/^0+/, '');
    if (!id) return undefined;
    ids.push(id);
  }
  return JSON.stringify([...new Set(ids)].sort());
}

function refinementContext(state: PeopleModelState, params: PersonSearchRequest) {
  const previous = state.appliedSearch;
  if (!previous?.search?.workflow_id || !sameAccount(previous.accountScope, currentAccountScope())) return undefined;
  const cohort = organizationCohort(state.appliedRequest?.organizations);
  return cohort && cohort === organizationCohort(params.organizations) ? previous.search : undefined;
}

function* fetchRelatedPeople(
  { payload, look_for, analytics: intent }: AnyAction,
  { call, put, select }: any,
  service: (...args: any[]) => Promise<any>,
  field: 'product_ids' | 'product_list_item_ids',
  both: boolean,
): Generator<any, void, any> {
  const state: PeopleModelState = yield select((state: any) => state.people);
  const params = { [field]: [...payload], ...(both ? {} : { look_for }) };
  const criteria = filterSnapshot(params, { ...state.filterMetadata,
    provenance: { ...state.filterMetadata?.provenance, [field]: 'inherited_company_selection' } });
  const run = beginSearch({ surface: 'people_results', params, criteria,
    trigger: intent?.trigger, context: intent?.context, action: intent?.action,
    execution: intent?.execution, executionKind: intent?.executionKind, workflowType: 'people_prospecting' });
  const request = contextForPageRequest(run, { page: 1, pageSize: state.pageSize, phase: 'submit', trigger: intent?.trigger });
  yield put({ type: 'updateState', payload: { appliedSearch: run, appliedRequest: undefined, pageRequest: request,
    resultsPage: undefined, peopleData: { pages: {}, total: null }, currentPage: 1, selectedRows: [] } });
  try {
    const response: any = yield call(service, params, request.analyticsContext);
    const latest: PeopleModelState = yield select((state: any) => state.people);
    if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== request.id) return;
    const companies = [
      ...((both || look_for === 'marketer') ? response.marketers || [] : []),
      ...((both || look_for === 'manufacturer') ? response.manufacturers || [] : []),
    ];
    const options = [...new Map(companies.filter((company: any) => company.forager_id)
      .map((company: any) => [String(company.forager_id), {
        value: String(company.forager_id), label: company.name || String(company.forager_id),
      }])).values()] as Array<{ value: string; label: string }>;
    const appliedRequest = immutableContext({ organizations: options.map((option) => Number(option.value)), role_is_current: true });
    yield put({ type: 'updateState', payload: { organizationOptions: options,
      filters: { ...latest.filters, organizations: options.map((option) => option.value), role_is_current: true },
      appliedRequest, filterMetadata: { ...rememberFilterOptions(latest.filterMetadata, 'organizations', options),
        provenance: { ...latest.filterMetadata?.provenance, organizations: 'inherited_company_selection', role_is_current: 'system_scope' } } } });
    let people: SimplifiedPersonSearchResponse | undefined = response.people;
    let pageRequest = request;
    let pageResponse = response;
    if (!Array.isArray(people?.search_results) && options.length) {
      pageRequest = contextForPageRequest(run, { page: 1, pageSize: state.pageSize, phase: 'page',
        trigger: 'automatic_batch', execution: executionForResponse(response) });
      people = yield call(searchPeople, { ...appliedRequest, page: 1 }, pageRequest.analyticsContext);
      pageResponse = people;
      const current: PeopleModelState = yield select((state: any) => state.people);
      if (!isCurrentSearch(run, current.appliedSearch) || current.pageRequest?.id !== request.id) return;
    }
    if (Array.isArray(people?.search_results)) {
      const view = resultsPageForResponse(run, pageRequest, pageResponse, peopleResultCount(people!));
      yield put({ type: 'setPersonsForPage', payload: { page: 1, data: people!.search_results,
        total: people!.total_search_results, runId: run.id, pageSize: state.pageSize, analytics: view } });
      yield put({ type: 'updateState', payload: { resultsPage: view, pageRequest: undefined } });
    } else yield put({ type: 'updateState', payload: { pageRequest: undefined } });
  } catch (error) {
    const latest: PeopleModelState = yield select((state: any) => state.people);
    if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== request.id) return;
    yield put({ type: 'updateState', payload: { pageRequest: undefined } });
    message.error('Failed to fetch related people');
  }
}

const Model: PeopleModelType = {
  namespace: 'people',
  state: {
    searchValue: '',
    filters: {},
    peopleData: {
      pages: {},
      total: 0,
    },
    currentPage: 1,
    pageSize: 10,
    selectedRows: [],
    filterDrawerVisible: false,
    peopleLists: [],
    listModalVisible: false,
    createListModalVisible: false,
    addingToList: false,
    organizationOptions: [],
  },
  effects: {
    *fetchPeople({ payload, analytics: suppliedIntent }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
      const state: PeopleModelState = yield select((state: any) => state.people);
      const intent: SearchIntent | undefined = suppliedIntent || state.searchIntent;
      // Analytics and UI revision fields never become provider criteria.
      const { _timestamp, analytics: _analytics, analyticsContext: _context, ...criteriaPayload } = payload || {};
      const searchParams: PersonSearchRequest = {
        ...state.filters, ...criteriaPayload, page: 1,
        person_name: criteriaPayload.person_name ?? (state.searchValue || undefined),
      };
      const invalidRanges = invalidFilterRanges(searchParams as Record<string, unknown>);
      const hasValidParams = Object.entries(searchParams).some(([key, value]) =>
        key !== 'role_is_current' && key !== 'page' && value !== undefined && value !== null &&
        value !== '' && (!Array.isArray(value) || value.length > 0));
      if (!hasValidParams || invalidRanges.length) {
        recordFilterApplyBlocked('people_results', invalidRanges.length ? 'invalid_range' : 'empty_criteria', invalidRanges);
        message.error('Please provide valid search terms or apply filters before searching.');
        return;
      }
      const metadata = rememberFilterOptions(state.filterMetadata, 'organizations', state.organizationOptions);
      const criteria = filterSnapshot(searchParams as Record<string, unknown>, metadata);
      const inherited = !intent?.context && !intent?.action &&
        (intent?.trigger === 'search_button' || intent?.trigger === 'apply_filters')
        ? refinementContext(state, searchParams) : undefined;
      let context = intent?.context || inherited;
      const interactive = intent?.executionKind ? intent.executionKind === 'interactive' :
        intent?.trigger !== 'initial_load' && intent?.trigger !== 'deep_link';
      const action = intent?.action || (context && intent?.trigger && interactive
        ? beginAction({ surface: 'people_results', context, trigger: intent.trigger, destination: context.destination })
        : undefined);
      // A retired origin cannot be revived by an otherwise unchanged cohort. Explicit caller
      // context still passes through T1 validation; an inferred stale origin starts independently.
      if (inherited && !action && interactive) context = undefined;
      const run = beginSearch({ surface: 'people_results', params: searchParams, criteria,
        trigger: intent?.trigger as any, context, action,
        execution: intent?.execution, executionKind: intent?.executionKind, workflowType: 'people_prospecting' });
      const pageRequest = contextForPageRequest(run, { page: 1, pageSize: state.pageSize, phase: 'submit', trigger: intent?.trigger as any });
      if (intent?.trigger === 'apply_filters') recordFiltersApplied({ surface: 'people_results',
        criteria, context: { ...run.search, ...run.action }, reason: intent.applyReason });
      yield put({ type: 'updateState', payload: { peopleData: { pages: {}, total: null },
        appliedSearch: run, appliedRequest: run.params, pageRequest, resultsPage: undefined,
        currentPage: 1, searchIntent: undefined, contactResults: {} } });
      yield put({ type: 'clearSelectedRows' });
      try {
        const response: SimplifiedPersonSearchResponse = yield call(searchPeople, { ...searchParams }, pageRequest.analyticsContext);
        const latest: PeopleModelState = yield select((state: any) => state.people);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        const view = resultsPageForResponse(run, pageRequest, response, peopleResultCount(response));
        yield put({ type: 'setPersonsForPage', payload: { page: 1, data: response.search_results,
          total: response.total_search_results, runId: run.id, pageSize: state.pageSize, analytics: view } });
        yield put({ type: 'updateState', payload: { currentPage: 1, resultsPage: view, pageRequest: undefined } });
      } catch (error) {
        const latest: PeopleModelState = yield select((state: any) => state.people);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch people data');
      }
    },
    *changePage({ payload, analytics: intent }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
      const state: PeopleModelState = yield select((state: any) => state.people);
      const target = payload.page;
      if (!Number.isSafeInteger(target) || target < 1) return;
      if ((target === state.currentPage && !state.pageRequest && intent?.trigger !== 'retry') || state.pageRequest?.page === target) return;
      const run = state.appliedSearch;
      const cached = state.peopleData.pages[target];
      if (!run) {
        if (cached) yield put({ type: 'updateState', payload: { currentPage: target, resultsPage: undefined } });
        return;
      }
      if (!state.appliedRequest || !isCurrentSearch(run, run)) return;
      const request = contextForPageRequest(run, { page: target, pageSize: state.pageSize,
        previousPage: state.currentPage, phase: 'page', trigger: intent?.trigger,
        action: intent?.action, navigationMethod: intent?.navigationMethod || navigationMethod(state.currentPage, target) });
      if (cached?.runId === run.id && intent?.trigger !== 'retry') {
        const view = cached.analytics ? resultsPageFromCache(cached.analytics, request) : undefined;
        yield put({ type: 'updateState', payload: { currentPage: target, resultsPage: view, pageRequest: undefined } });
        return;
      }
      yield put({ type: 'updateState', payload: { pageRequest: request } });
      try {
        const response: SimplifiedPersonSearchResponse = yield call(searchPeople,
          { ...state.appliedRequest, page: target }, request.analyticsContext);
        const latest: PeopleModelState = yield select((state: any) => state.people);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== request.id) return;
        const view = resultsPageForResponse(run, request, response, peopleResultCount(response));
        yield put({ type: 'setPersonsForPage', payload: { page: target, data: response.search_results,
          total: response.total_search_results, runId: run.id, pageSize: state.pageSize, analytics: view } });
        yield put({ type: 'updateState', payload: { currentPage: target, resultsPage: view, pageRequest: undefined } });
      } catch (error) {
        const latest: PeopleModelState = yield select((state: any) => state.people);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== request.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch people data for page');
      }
    },
    *fetchPerson(
      { callback, payload, isFreePlan, analyticsContext }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        let state = yield select((state: any) => state.people);
        const { peopleData } = state;

        const isEmailUnlocked = Object.values(peopleData.pages).some((pageData: any) => {
          return pageData.data.some(
            (person: SimplifiedPerson) =>
              person.person_id === payload && isPeopleEmailUnlocked(person),
          );
        });

        if (isEmailUnlocked) {
          callback?.();
          return;
        }

        const subscriptionState = yield select((state: any) => state.subscription);
        const { people_credits_remaining } = subscriptionState;
        const emailCreditCost = getRequiredCredits('email', subscriptionState);

        const response: SimplifiedPerson = yield call(fetchPerson, payload, isFreePlan, analyticsContext);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        const previousContexts = yield select((state: any) => state.people.contactResults || {});
        yield put({ type: 'updateState', payload: { contactResults: { ...previousContexts,
          [`${payload}:email`]: { analyticsContext, execution: executionForResponse(response) },
        } } });

        yield put({ type: 'mergePersonDetails', payload: {
          personId: response.person_id || payload,
          details: { ...response, viewed: true, email_unlocked: response.success === true },
        } });

        callback?.(response, { analyticsContext, execution: executionForResponse(response) });

        const sharedUnlockMessage = getSharedUnlockMessage(response);
        if (sharedUnlockMessage) message.info(sharedUnlockMessage);

        if (response.work_email) {
          yield put({
            type: 'subscription/deductCredits',
            payload: {
              people_credits_remaining: getPeopleCreditsAfterUnlock(
                response,
                people_credits_remaining,
                emailCreditCost,
              ),
            },
          });
        }

        // DEBUG ONLY:
        // if (response.work_email) {
        //     message.info('Fetched from: ' + response.email_fetched_from);
        // }
      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        if (!handlePeopleCreditError(error, 'email')) {
          message.error('Failed to fetch person data');
        }
      }
    },
    *fetchPhone(
      { payload, callback, analyticsContext }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        /*
                 premilanary logic to check if the person is viewed and credits etc.
                */

        let state = yield select((state: any) => state.people);
        const { peopleData } = state;

        const isPhoneUnlocked = Object.values(peopleData.pages).some((pageData: any) => {
          return pageData.data.some(
            (person: SimplifiedPerson) =>
              person.person_id === payload.person_id && isPeoplePhoneUnlocked(person),
          );
        });

        if (payload.phone_numbers && payload.phone_numbers.length > 0 && isPhoneUnlocked) {
          callback?.();
          return;
        }

        const subscriptionState = yield select((state: any) => state.subscription);
        const { people_credits_remaining } = subscriptionState;
        const phoneCreditCost = getRequiredCredits('phone', subscriptionState);

        const response: any = yield call(fetchPhone, { person_id: payload.person_id }, analyticsContext);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        const previousContexts = yield select((state: any) => state.people.contactResults || {});
        yield put({ type: 'updateState', payload: { contactResults: { ...previousContexts,
          [`${payload.person_id}:phone`]: { analyticsContext, execution: executionForResponse(response) },
        } } });

        if (response.success) {
          yield put({ type: 'mergePersonDetails', payload: {
            personId: payload.person_id,
            details: { phone_numbers: response.phone_numbers, viewed: true, phone_unlocked: true },
          } });

          callback?.(response, { analyticsContext, execution: executionForResponse(response) });

          const sharedUnlockMessage = getSharedUnlockMessage(response);
          if (sharedUnlockMessage) message.info(sharedUnlockMessage);

          if (response.success) {
            yield put({
              type: 'subscription/deductCredits',
              payload: {
                people_credits_remaining: getPeopleCreditsAfterUnlock(
                  response,
                  people_credits_remaining,
                  phoneCreditCost,
                ),
              },
            });
          }
        } else {
          message.error('No mobile number found');
          callback?.(response, { analyticsContext, execution: executionForResponse(response) });
        }

      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        if (!handlePeopleCreditError(error, 'phone')) {
          message.error('No mobile number found');
        }
      }
    },
    *fetchWorkEmail(
      { payload, callback, analyticsContext }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        const subscriptionState = yield select((state: any) => state.subscription);
        const { people_credits_remaining } = subscriptionState;
        const emailCreditCost = getRequiredCredits('email', subscriptionState);

        const response: any = yield call(fetchWorkEmail, { person_id: payload.person_id }, analyticsContext);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        const previousContexts = yield select((state: any) => state.people.contactResults || {});
        yield put({ type: 'updateState', payload: { contactResults: { ...previousContexts,
          [`${payload.person_id}:email`]: { analyticsContext, execution: executionForResponse(response) },
        } } });

        if (response?.success !== undefined) {
          yield put({ type: 'mergePersonDetails', payload: {
            personId: payload.person_id,
            details: {
              ...(response.work_email !== undefined && { work_email: response.work_email }),
              ...(response.work_email_status !== undefined && { work_email_status: response.work_email_status }),
              ...(response.emails !== undefined && { emails: response.emails }),
              viewed: true, email_unlocked: response.success === true,
            },
          } });

          callback?.(response, { analyticsContext, execution: executionForResponse(response) });

          const sharedUnlockMessage = getSharedUnlockMessage(response);
          if (sharedUnlockMessage) message.info(sharedUnlockMessage);

          if (response.work_email) {
            yield put({
              type: 'subscription/deductCredits',
              payload: {
                people_credits_remaining: getPeopleCreditsAfterUnlock(
                  response,
                  people_credits_remaining,
                  emailCreditCost,
                ),
              },
            });
          } else {
            message.error('No work email found');
          }
        }
      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        if (!handlePeopleCreditError(error, 'email')) {
          message.error('Failed to fetch email');
        }
      }
    },
    *fetchPeopleListsV2(_: AnyAction, { call, put }: any): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        const response: any[] = yield call(fetchPeopleListsV2);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;

        yield put({
          type: 'setPeopleLists',
          payload: response,
        });
      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        message.error('Failed to fetch people lists');
        console.error('Error fetching people lists:', error);
      }
    },
    *createPeopleList(
      { payload, callback, analyticsContext }: AnyAction,
      { call, put }: any,
    ): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        const response: any = yield call(createPeopleListV2, { name: payload.name }, analyticsContext);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;

        yield put({
          type: 'fetchPeopleListsV2',
        });

        if (callback) {
          callback(response);
        }

        yield put({
          type: 'toggleCreateListModal',
          payload: false,
        });

        message.success('List created successfully');
      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        message.error('Failed to create list');
        console.error('Error creating list:', error);
      }
    },
    *addPeopleToList(
      { payload, callback, analyticsContext }: AnyAction,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const initiatingAccount = currentAccountScope();
      try {
        yield put({
          type: 'setAddingToList',
          payload: true,
        });

        const response: any = yield call(addPeopleToListV2, {
          listId: payload.listId,
          peopleIds: payload.peopleIds,
        }, analyticsContext);
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;

        if (response.credits_to_deduct > 0) {
          yield put({
            type: 'subscription/deductCredits',
            payload: {
              people_credits_remaining:
                response.people_credits_remaining ??
                (yield select((state: any) => state.subscription.people_credits_remaining)) -
                  response.credits_to_deduct,
            },
          });
        }

        // Get people IDs from response
        const peopleFromResponse = response.people || [];
        const updatedPeopleIds = peopleFromResponse.map((p: SimplifiedPerson) => p.person_id);

        // Get all the peopleData
        const peopleData = yield select((state: any) => state.people.peopleData);

        // Create deep copy of pages to safely modify
        const updatedPages = JSON.parse(JSON.stringify(peopleData.pages));

        // Update viewed status for each person across all pages
        Object.keys(updatedPages).forEach((pageKey) => {
          const page = updatedPages[pageKey];
          if (page && page.data) {
            updatedPages[pageKey].data = page.data.map((person: SimplifiedPerson) => {
              if (updatedPeopleIds.includes(person.person_id)) {
                // Find this person in the response data for complete information
                const updatedPerson = peopleFromResponse.find(
                  (p: SimplifiedPerson) => p.person_id === person.person_id,
                );

                // Merge with response data if available, always set viewed: true
                return {
                  ...person,
                  ...(updatedPerson || {}),
                  viewed: true,
                };
              }
              return person;
            });
          }
        });

        // Update state with modified data
        yield put({
          type: 'updateState',
          payload: {
            peopleData: {
              ...peopleData,
              pages: updatedPages,
            },
          },
        });

        yield put({
          type: 'toggleListModal',
          payload: false,
        });

        yield put({
          type: 'clearSelectedRows',
        });

        yield put({
          type: 'setAddingToList',
          payload: false,
        });

        if (callback) {
          callback(response);
        }

        if (response && response.message) {
          message.success(response.message);
        } else {
          message.success(`Added ${payload.peopleIds.length} people to list`);
        }
      } catch (error) {
        if (initiatingAccount && !sameAccount(initiatingAccount, currentAccountScope())) return;
        yield put({
          type: 'setAddingToList',
          payload: false,
        });

        message.error('Failed to add people to list');
        console.error('Error adding people to list:', error);
      }
    },
    *addAllPeopleToSelectedRows(_: AnyAction, { put, select }: any): Generator<any, void, any> {
      try {
        const state = yield select((state: any) => state.people);
        const allRows: number[] = [];

        Object.values(state.peopleData.pages).forEach((page: any) => {
          page.data.forEach((person: SimplifiedPerson) => {
            allRows.push(person.person_id);
          });
        });

        yield put({
          type: 'updateState',
          payload: {
            selectedRows: [...new Set(allRows)],
          },
        });
      } catch (error) {
        console.error('Error adding all people to selected rows:', error);
      }
    },
    *getSelectedPeopleFromIds({ callback }: AnyAction, { select }: any): Generator<any, void, any> {
      try {
        const state = yield select((state: any) => state.people);
        const { selectedRows, peopleData } = state;

        // Get all people data from all pages
        const allPeople: SimplifiedPerson[] = [];
        Object.values(peopleData.pages).forEach((page: any) => {
          page.data.forEach((person: SimplifiedPerson) => {
            if (selectedRows.includes(person.person_id)) {
              allPeople.push(person);
            }
          });
        });

        if (callback) {
          callback(allPeople);
        }
      } catch (error) {
        console.error('Error getting selected people:', error);
        if (callback) {
          callback([]);
        }
      }
    },
    *fetchPeopleFromProductList(action: AnyAction, effects: any): Generator<any, void, any> {
      yield* fetchRelatedPeople(action, effects, fetchPeopleFromProductList, 'product_list_item_ids', false);
    },
    *fetchPeopleFromProductListBoth(action: AnyAction, effects: any): Generator<any, void, any> {
      yield* fetchRelatedPeople(action, effects, fetchPeopleFromProductListBoth, 'product_list_item_ids', true);
    },
    *fetchPeopleFromProducts(action: AnyAction, effects: any): Generator<any, void, any> {
      yield* fetchRelatedPeople(action, effects, fetchPeopleFromProducts, 'product_ids', false);
    },
    *fetchPeopleFromProductsBoth(action: AnyAction, effects: any): Generator<any, void, any> {
      yield* fetchRelatedPeople(action, effects, fetchPeopleFromProductsBoth, 'product_ids', true);
    },
  },

  reducers: {
    updateState(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      if (
        Object.keys(payload).every((key) => state[key as keyof PeopleModelState] === payload[key])
      ) {
        return state;
      }
      return {
        ...state,
        ...payload,
      };
    },
    updateSearchValue(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      if (state.searchValue === payload) {
        return state;
      }
      return {
        ...state,
        searchValue: payload,
      };
    },
    updateSearchValueOnly(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      if (state.searchValue === payload) {
        return state;
      }
      return {
        ...state,
        searchValue: payload,
      };
    },
    setPersonsForPage(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      const { page, data, total } = payload;

      return {
        ...state,
        peopleData: {
          ...state.peopleData,
          pages: {
            ...state.peopleData.pages,
            [page]: {
              ...state.peopleData.pages[page],
              ...(payload.runId ? { runId: payload.runId, pageSize: payload.pageSize, analytics: payload.analytics } : {}),
              data,
              total,
            },
          },
          total,
        },
      };
    },
    // Merge into currently retained pages without restoring an old search/page snapshot.
    // Page identity and totals remain unchanged, so enrichment cannot create another view.
    mergePersonDetails(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      return { ...state, peopleData: { ...state.peopleData,
        pages: Object.fromEntries(Object.entries(state.peopleData.pages).map(([key, page]) => [key, {
          ...page, data: page.data.map((person) => person.person_id === payload.personId
            ? { ...person, ...payload.details } : person),
        }])),
      } };
    },
    resetFilters(state: PeopleModelState): PeopleModelState {
      return {
        ...state,
        appliedSearch: undefined, appliedRequest: undefined, pageRequest: undefined, resultsPage: undefined,
        searchIntent: undefined, filterMetadata: {}, contactResults: {},
        filters: {},
        searchValue: '',
        peopleData: {
          pages: {},
          total: 0,
        },
        currentPage: 1,
        organizationOptions: [],
      };
    },
    toggleFilterDrawer(state: PeopleModelState): PeopleModelState {
      return {
        ...state,
        filterDrawerVisible: !state.filterDrawerVisible,
      };
    },
    updateFilter(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      const { field, value } = payload;
      const shouldRemoveFilter =
        field === 'role_title' && (value === undefined || value === null || value === '');

      if (shouldRemoveFilter) {
        if (!(field in state.filters)) {
          return state;
        }

        const filters = { ...state.filters };
        delete filters[field as keyof Partial<PersonSearchRequest>];

        return {
          ...state,
          filters,
        };
      }

      if (state.filters[field as keyof Partial<PersonSearchRequest>] === value) {
        return state;
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [field]: value,
        },
      };
    },
    addToSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      if (state.selectedRows.includes(payload)) {
        return state;
      }

      return {
        ...state,
        selectedRows: [...state.selectedRows, payload],
      };
    },
    removeFromSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      if (!state.selectedRows.includes(payload)) {
        return state;
      }

      return {
        ...state,
        selectedRows: state.selectedRows.filter((id) => id !== payload),
      };
    },
    clearSelectedRows(state: PeopleModelState): PeopleModelState {
      if (state.selectedRows.length === 0) {
        return state;
      }

      return {
        ...state,
        selectedRows: [],
      };
    },
    setSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      const newSelectedRows = Array.isArray(payload) ? payload.map(Number) : [];

      if (JSON.stringify(state.selectedRows) === JSON.stringify(newSelectedRows)) {
        return state;
      }

      return {
        ...state,
        selectedRows: newSelectedRows,
      };
    },
    setPeopleLists(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      return {
        ...state,
        peopleLists: payload,
      };
    },
    toggleListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      const newValue = payload !== undefined ? payload : !state.listModalVisible;
      return {
        ...state,
        listModalVisible: newValue,
      };
    },
    toggleCreateListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      const newValue = payload !== undefined ? payload : !state.createListModalVisible;
      return {
        ...state,
        createListModalVisible: newValue,
      };
    },
    setAddingToList(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
      return {
        ...state,
        addingToList: payload,
      };
    },
    initializeFromAccountsContext(
      state: PeopleModelState,
      { payload }: AnyAction,
    ): PeopleModelState {
      return {
        ...state,
        appliedSearch: undefined, appliedRequest: undefined, pageRequest: undefined, resultsPage: undefined,
        searchIntent: payload.analytics,
        filterMetadata: { ...rememberFilterOptions({}, 'organizations', payload.organizationOptions),
          provenance: { organizations: 'inherited_company_selection', role_is_current: 'system_scope' } }, contactResults: {},
        searchValue: '',
        filters: {
          organizations: payload.organizations,
          role_is_current: true,
        },
        peopleData: {
          pages: {},
          total: 0,
        },
        currentPage: 1,
        selectedRows: [],
        organizationOptions: payload.organizationOptions,
      };
    },
    resetAll(state: PeopleModelState): PeopleModelState {
      return {
        ...state,
        appliedSearch: undefined, appliedRequest: undefined, pageRequest: undefined, resultsPage: undefined,
        searchIntent: undefined, filterMetadata: {}, contactResults: {},
        searchValue: '',
        filters: {},
        peopleData: {
          pages: {},
          total: 0,
        },
        currentPage: 1,
        pageSize: 10,
        selectedRows: [],
        filterDrawerVisible: false,
        peopleLists: [],
        listModalVisible: false,
        createListModalVisible: false,
        addingToList: false,
        organizationOptions: [],
      };
    },
  },
};

export default Model;
